import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import React, { useCallback, useEffect, useState } from 'react';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';
import {
  Alert,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { BrandLogo, PrimaryCTA } from '../../components';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { fpColors } from '../../styles/theme';
import { clarityHelpers } from '../../utils/clarity';
import { logger } from '../../utils/logger';
import { selectUser } from '../auth/authSlice';
import {
  loadDashboardData,
  refreshDashboardData,
  selectAchievementStats,
  selectDashboardRefreshing,
  selectInventorySummaryStats,
} from './dashboardSlice';
import { NotificationBell } from '../notifications/components/NotificationBell';
import { NotificationCard } from '../notifications/components/NotificationCard';
import { useNotificationDeepLink } from '../notifications/useNotificationDeepLink';
import { useNotificationActions } from '../notifications/useNotificationActions';
// FE-227 v2 — decline-with-notes sheet for Recent Activity feed.
import { DenyNotesSheet } from '../borrowRequests/components/DenyNotesSheet';
import { ShareRow } from '../sharing/components/ShareRow';
import {
  loadUserShares,
  selectOutboundActiveShares,
  selectOutboundActiveCount,
} from '../sharing/sharingSlice';
import { selectGroups } from '../sharing/groupsSlice';
import type { SharedByMe } from '../../api/sharingTypes';
import {
  loadNotifications,
  selectTopFiveNotifications,
  selectUnreadCount,
  selectIsLoadingList,
} from '../notifications/notificationsSlice';
// FE-213 — Dashboard banners for pending borrow requests + active
// recalls. v1 ships ONLY the owner-side "N pending borrow requests"
// banner (yellow). The borrower-side recall banner is gated on
// backend#286 (has_active_recall field) and lands in a follow-up.
import {
  loadReceivedRequests,
  selectReceivedRequests,
} from '../borrowRequests/borrowRequestsSlice';
// FE #198 / Sub 3 — Borrower-side recall banner. Pulls from the
// browse-slice inventory cache (already loaded by `loadDashboardData`
// → `refreshDashboardData` indirectly via the browse hydration that
// the items grid uses). Filters for items where the viewer is the
// custodian AND has_active_recall is true.
import { selectInventoryItemsEnhanced } from '../browse/browseSlice';
import { RecallBanner } from '../inventory/components/RecallBanner';
// FE #124.4 — Scan recovery banner. Shown when the user has ≥1
// unfinished scan (status IN_PROGRESS or READY_TO_SAVE). Pulls from
// scansSlice; the slice's loadScans thunk runs on Dashboard mount
// (below). The banner self-hides when there are no unfinished scans.
import {
  loadScans,
  discardScan,
  optimisticallyRemoveScan,
  selectUnfinishedScans,
} from '../scans/scansSlice';
import { ScanRecoveryBanner } from '../scans/components/ScanRecoveryBanner';
// FE #124.5 — Top-bar 🕒 clock-rewind icon (always visible).
import { ScanHistoryButton } from '../scans/components/ScanHistoryButton';

const DESKTOP_BREAKPOINT = 768;


export const DashboardScreen: React.FC = () => {
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const { width } = useWindowDimensions();
  const isDesktop = width >= DESKTOP_BREAKPOINT;

  const user = useAppSelector(selectUser);
  const achievements = useAppSelector(selectAchievementStats);
  const isRefreshing = useAppSelector(selectDashboardRefreshing);
  const inventorySummary = useAppSelector(selectInventorySummaryStats);
  const totalItems = inventorySummary?.total_items ?? 0;

  // Sprint 8 / FE-129 — Recent activity is now notification-backed.
  // The bell polls unread-count every 60s; the Dashboard pulls the
  // top-5 list on mount + pull-to-refresh. When BE-151 isn't deployed
  // the slice's 404-fallback keeps these empty (clean empty state).
  const topFiveNotifs = useAppSelector(selectTopFiveNotifications);
  const totalUnread = useAppSelector(selectUnreadCount);
  const notifsLoading = useAppSelector(selectIsLoadingList);
  const notifActions = useNotificationActions();

  // Phase D.2 — "Recent shares I sent" mini-section. Pulls from the
  // same /users/shares cache the Share-tab sections already load
  // (Phase C). Shown only when the user has outbound active shares;
  // silent otherwise. Answers the user's "I sent X to Y — where do
  // I see my own action?" gap without needing a new backend notif
  // type.
  const myActiveShares = useAppSelector(selectOutboundActiveShares);
  const myActiveSharesCount = useAppSelector(selectOutboundActiveCount);

  // FE-213 — Owner-side: count PENDING borrow requests on the viewer's
  // items so the Dashboard can show a banner. Filter client-side so
  // the banner stays in sync as the slice is mutated (approval /
  // decline / cancellation all flip the row's status).
  const receivedRequests = useAppSelector(selectReceivedRequests);
  const pendingReceivedCount = (() => {
    if (!Array.isArray(receivedRequests)) return 0;
    return receivedRequests.filter(
      (r: any) => (r?.status ?? '').toUpperCase() === 'PENDING',
    ).length;
  })();
  const activeRecallCount = (() => {
    if (!Array.isArray(receivedRequests)) return 0;
    return receivedRequests.filter(
      (r: any) =>
        (r?.priority ?? '').toUpperCase() === 'RECALL' &&
        (r?.status ?? '').toUpperCase() === 'PENDING',
    ).length;
  })();
  // Pending count for the yellow banner excludes recalls (they get
  // their own red banner so each surface speaks to one decision).
  const pendingNonRecallCount = Math.max(
    0,
    pendingReceivedCount - activeRecallCount,
  );

  // FE #198 / Sub 3 — Borrower-side recall banner. The user can be on
  // BOTH sides at once (owner who recalled X + custodian of someone
  // else's Y that they recalled). We scan the loaded inventory for
  // rows where:
  //   - has_active_recall === true (BE #286 denormalization)
  //   - current_custodian_user_id === viewer (they're the holder)
  //   - user_id !== viewer (they're NOT the owner — owner-side is the
  //     yellow/red banner above; this banner is about items the
  //     viewer needs to RETURN)
  const allInventory = useAppSelector(selectInventoryItemsEnhanced) as any[];
  const itemsToReturnForRecall = (() => {
    if (!Array.isArray(allInventory) || !user?.id) return [];
    return allInventory.filter(
      (it) =>
        it?.has_active_recall === true &&
        it?.active_recall != null &&
        it?.current_custodian_user_id === user.id &&
        it?.user_id !== user.id,
    );
  })();

  useEffect(() => {
    logger.info('🏠 Dashboard: loading data');
    dispatch(loadDashboardData());
    // Pull the latest notifications so the "Recent activity" block
    // is fresh on mount. Bell's 60s polling only refreshes the count.
    dispatch(loadNotifications({ mode: 'replace' }));
    // Phase D.2 — pull /users/shares so the "Recent shares I sent"
    // mini-section has fresh data. No-op if Phase C already loaded
    // it on the Share tab.
    dispatch(loadUserShares({} as any));
    // FE-213 — Pull received borrow requests so the pending-requests
    // banner has fresh data on cold mount. No-op if BorrowRequestsManager
    // already loaded it elsewhere in the session.
    dispatch(loadReceivedRequests() as any);
    // FE #124.4 — Load the latest scans so the ScanRecoveryBanner can
    // render if the user has any unfinished scans (IN_PROGRESS or
    // READY_TO_SAVE). Limit=10 covers the practical case — unfinished
    // scans are always the most recent (newest-first sort), so the
    // top page captures them.
    dispatch(loadScans({ mode: 'replace', params: { limit: 10 } }));
  }, [dispatch]);

  // FE-255 PR5 — refetch on focus. Dashboard is the entry point for
  // most navigations, so users re-land here often; without this, the
  // pending-requests count + scan-recovery banner can sit stale for
  // minutes after the user resolves the underlying state on another
  // screen. 30s debounce is plenty for "user came back from another
  // tab" while still cheap for back-and-forth navigation.
  const refetchDashboard = useCallback(() => {
    dispatch(refreshDashboardData());
    dispatch(loadNotifications({ mode: 'replace' }));
    dispatch(loadReceivedRequests() as any);
    dispatch(loadScans({ mode: 'replace', params: { limit: 10 } }));
  }, [dispatch]);
  useRefetchOnFocus(refetchDashboard);

  const handleRefresh = () => {
    clarityHelpers.trackInteraction('dashboard_refresh', 'pull_to_refresh');
    dispatch(refreshDashboardData());
    // FE-129 — pull-to-refresh also refreshes the activity block.
    dispatch(loadNotifications({ mode: 'replace' }));
    // FE-213 — refresh the pending-requests count too.
    dispatch(loadReceivedRequests() as any);
    // FE #124.4 — refresh the scan-recovery banner data too.
    dispatch(loadScans({ mode: 'replace', params: { limit: 10 } }));
  };

  // FE-129 — notification card tap → deep-link. Shared with
  // NotificationsScreen + CrewHubScreen via the same hook so all
  // three screens stay in sync (the SHARE_RECEIVED dead-link bug
  // that shipped earlier was caused by 3 copies drifting from each
  // other — single hook prevents that class of bug).
  const handleNotifPress = useNotificationDeepLink();

  const goTo = (route: string, params?: any) => {
    clarityHelpers.trackInteraction(`dashboard_${route.toLowerCase()}`, 'click');
    (navigation as any).navigate(route, params);
  };

  // FE #124.4 — Unfinished scans for the ScanRecoveryBanner. Memoized
  // selector at the slice level so unrelated polling ticks don't
  // re-render the banner. Loaded by the `loadScans` dispatch in the
  // mount effect above.
  const unfinishedScans = useAppSelector(selectUnfinishedScans);

  // Discard the single unfinished scan straight from the banner (web +
  // phone-web tap target). Optimistic remove → banner self-hides; on
  // failure, refetch so the scan reappears. Confirm via window.confirm
  // on web (Alert.alert is a no-op there).
  const handleDiscardScan = (fileRecordId: number) => {
    const run = async () => {
      clarityHelpers.trackInteraction(
        'dashboard_scan_recovery_banner',
        'discard_single',
      );
      dispatch(optimisticallyRemoveScan(fileRecordId));
      try {
        await dispatch(discardScan(fileRecordId)).unwrap();
      } catch {
        dispatch(loadScans({} as any)); // refetch → scan reappears
        if (typeof window !== 'undefined' && window.alert) {
          window.alert('Couldn’t discard the scan. It’s still in your history.');
        }
      }
    };
    const msg = 'Discard this unfinished scan? It hasn’t been saved.';
    if (typeof window !== 'undefined' && window.confirm) {
      if (window.confirm(msg)) run();
    } else {
      Alert.alert('Discard this scan?', msg, [
        { text: 'Keep', style: 'cancel' },
        { text: 'Discard', style: 'destructive', onPress: run },
      ]);
    }
  };

  const firstName = (user?.full_name || '').trim().split(' ')[0] || 'there';

  // progress_to_next_rank is a 0-100 number per the slice; use it as the
  // "% verified" proxy until a real verification metric is exposed.
  const rawProgress = achievements?.progress_to_next_rank ?? 0;
  const verifiedPct = Math.max(0, Math.min(100, Math.round(rawProgress)));
  const itemsLine =
    totalItems > 0
      ? `${totalItems.toLocaleString()} ${totalItems === 1 ? 'item' : 'items'} in your inventory`
      : null;
  const verifiedLine =
    verifiedPct > 0 ? `${verifiedPct}% verified today` : null;
  // FE #151 — pick the right empty-state copy for users who have
  // 0 personal items but DO have access via crew membership. The old
  // "Start adding items" copy was wrong for crew members who already
  // see 100+ items via Browse by Room from their groups.
  const groups = useAppSelector(selectGroups);
  const crewItemCount = groups.reduce(
    (sum, g) => sum + (g.item_count ?? 0),
    0,
  );
  const fallbackCopy =
    crewItemCount > 0
      ? 'Browse items shared with you, or add your own.'
      : 'Start adding items to build your inventory.';
  const subtitleText =
    [itemsLine, verifiedLine].filter(Boolean).join(' · ') || fallbackCopy;

  const initials = ((user?.full_name || 'U')
    .split(' ')
    .map((s) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()) || 'U';

  return (
    <View style={styles.page}>
      <StatusBar barStyle="dark-content" />

      {/* Sticky top app bar — matches Stitch header (brand left, avatar right) */}
      <View style={styles.topBar}>
        <View style={styles.topBarInner}>
          <View style={styles.brandRow}>
            <BrandLogo variant="horizontal" height={24} tone="color" />
          </View>
          <View style={styles.topBarRight}>
            {/* FE #124.5 — Scan history shortcut. Always visible
                across every tab. Tap → /scans. */}
            <ScanHistoryButton
              onPress={() => (navigation as any).navigate('ScanHistory')}
            />
            <NotificationBell
              onPress={() => (navigation as any).navigate('Notifications')}
            />
            <Pressable
              onPress={() => (navigation as any).navigate('Profile')}
              style={styles.avatar}
              accessibilityRole="button"
              accessibilityLabel="Profile"
            >
              <Text style={styles.avatarText}>{initials}</Text>
            </Pressable>
          </View>
        </View>
      </View>

      <ScrollView
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={fpColors.primary}
          />
        }
      >
        {/* FE #124.4 — Scan-recovery banner. Renders when the user has
            ≥1 unfinished scan (status IN_PROGRESS or READY_TO_SAVE).
            Single-scan variant resumes directly into SegmentScreen;
            multi-scan variant routes to the /scans Scan History page.
            Pinned ABOVE the recall banners — losing scan progress is
            a fast-fade concern; recalls are more durable so they can
            sit slightly lower on the scroll. */}
        {unfinishedScans.length > 0 && (
          <ScanRecoveryBanner
            unfinished={unfinishedScans}
            onResumeSingle={(fileRecordId) => {
              clarityHelpers.trackInteraction(
                'dashboard_scan_recovery_banner',
                'resume_single',
              );
              // Route to the upload/segment screen. The resume wiring
              // itself lands in FE #124.6 — today this route opens
              // the regular Upload screen.
              goTo('Upload', { file_record_id: fileRecordId, resume: true });
            }}
            onViewAll={() => {
              clarityHelpers.trackInteraction(
                'dashboard_scan_recovery_banner',
                'view_all',
              );
              goTo('ScanHistory');
            }}
            onDiscardSingle={handleDiscardScan}
          />
        )}

        {/* FE #198 / Sub 3 — Borrower-side recall banner(s).
            Renders one compact RecallBanner per item the viewer holds
            with an active recall. Pinned to the top of the scroll so
            the urgent message lands BEFORE the yellow pending-requests
            banner (which is owner-side info). Multi-item case stacks
            vertically; each banner tap → ItemDetail for that item. */}
        {itemsToReturnForRecall.length > 0 && (
          <View testID="dashboard-recall-banners">
            {itemsToReturnForRecall.slice(0, 3).map((it: any) => (
              <RecallBanner
                key={it.id}
                itemId={Number(it.id)}
                recall={it.active_recall}
                itemName={it.item_name}
                variant="compact"
                onPress={() => {
                  clarityHelpers.trackInteraction(
                    'dashboard_recall_banner',
                    'tap',
                  );
                  goTo('ItemDetail', { itemId: String(it.id) });
                }}
              />
            ))}
            {itemsToReturnForRecall.length > 3 && (
              <Pressable
                onPress={() => goTo('MainTabs', { screen: 'Browse' })}
                style={({ pressed }) => [
                  styles.recallMoreLink,
                  pressed && { opacity: 0.85 },
                ]}
                accessibilityRole="button"
                accessibilityLabel={`${itemsToReturnForRecall.length - 3} more items with active recalls. Tap to review.`}
              >
                <Text style={styles.recallMoreText}>
                  + {itemsToReturnForRecall.length - 3} more items with active recalls
                </Text>
              </Pressable>
            )}
          </View>
        )}

        {/* FE-213 — Pending-requests banner (owner-side, yellow tone).
            Renders only when there are PENDING borrow requests on the
            user's items that AREN'T recalls. Recalls get their own
            urgent banner once backend#286 (has_active_recall) ships;
            for v1 they're folded into the same banner with a stronger
            label so the user still notices.
            Tap → BorrowRequestsManager. */}
        {pendingReceivedCount > 0 && (
          <Pressable
            onPress={() => {
              clarityHelpers.trackInteraction(
                'dashboard_pending_requests_banner',
                'tap',
              );
              goTo('SharingRequests');
            }}
            style={({ pressed }) => [
              styles.attentionBanner,
              activeRecallCount > 0 && styles.attentionBannerUrgent,
              pressed && { opacity: 0.85 },
            ]}
            accessibilityRole="button"
            accessibilityLabel={
              activeRecallCount > 0
                ? `${activeRecallCount} active recall${activeRecallCount > 1 ? 's' : ''}, ${pendingNonRecallCount} other pending borrow request${pendingNonRecallCount === 1 ? '' : 's'}. Tap to review.`
                : `${pendingReceivedCount} pending borrow request${pendingReceivedCount === 1 ? '' : 's'} on your items. Tap to review.`
            }
            testID="dashboard-pending-requests-banner"
          >
            <View
              style={[
                styles.attentionBannerIcon,
                activeRecallCount > 0 && styles.attentionBannerIconUrgent,
              ]}
            >
              <MaterialIcons
                name={activeRecallCount > 0 ? 'priority-high' : 'inbox'}
                size={18}
                color={
                  activeRecallCount > 0
                    ? fpColors.onErrorContainer ?? fpColors.error
                    : fpColors.onTertiaryContainer ?? fpColors.tertiary
                }
              />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.attentionBannerTitle}>
                {activeRecallCount > 0
                  ? `${activeRecallCount} active recall${activeRecallCount > 1 ? 's' : ''} ${activeRecallCount > 1 ? 'need' : 'needs'} your attention`
                  : `${pendingReceivedCount} pending borrow request${pendingReceivedCount === 1 ? '' : 's'}`}
              </Text>
              <Text style={styles.attentionBannerSubtitle}>
                {activeRecallCount > 0 && pendingNonRecallCount > 0
                  ? `Plus ${pendingNonRecallCount} other request${pendingNonRecallCount === 1 ? '' : 's'} · Tap to review`
                  : 'Tap to review and decide'}
              </Text>
            </View>
            <MaterialIcons
              name="chevron-right"
              size={20}
              color={fpColors.onSurfaceVariant}
            />
          </Pressable>
        )}

        {/* Hero: greeting + Add New Item CTA */}
        <View style={[styles.hero, isDesktop && styles.heroDesktop]}>
          <View style={styles.heroText}>
            <Text style={styles.h1}>Welcome back, {firstName}</Text>
            <Text style={styles.subtitle}>{subtitleText}</Text>
          </View>
          <View style={isDesktop ? styles.heroCtaDesktop : styles.heroCtaMobile}>
            <PrimaryCTA
              label="Add New Item"
              onPress={() => {
                // Straight to the Upload/camera screen on ALL platforms — no
                // intermediate chooser. That screen hosts both entry points the
                // chooser used to gate: pick-from-library (left dock) + Quick
                // add one item (right dock). Resume of an unfinished scan stays
                // reachable via the Dashboard ScanRecovery banner above.
                goTo('Upload');
              }}
              iconRight="qr-code-scanner"
            />
          </View>
        </View>


        {/* Sprint 8 / FE-129 — Recent activity.
            Replaces the old "Low Stock Alerts" (pure mock) and the
            fake "Recent Activity" (which was just last-5-items-added
            with fabricated action labels). Now reads from the real
            notifications feed (backend#151). Cap at 5; full inbox is
            one tap away via [View all]. */}
        <View style={styles.section}>
          <View style={styles.activityHeader}>
            <View style={styles.activityHeaderLeft}>
              <MaterialIcons
                name="notifications-active"
                size={18}
                color={fpColors.primary}
              />
              <Text style={styles.sectionTitle}>Recent activity</Text>
              {totalUnread > 0 && (
                <View style={styles.unreadPill}>
                  <Text style={styles.unreadPillText}>
                    {totalUnread > 99 ? '99+' : `${totalUnread} unread`}
                  </Text>
                </View>
              )}
            </View>
            {topFiveNotifs.length > 0 && (
              <Pressable
                onPress={() => goTo('Notifications')}
                hitSlop={8}
                accessibilityRole="button"
                accessibilityLabel="View all notifications"
              >
                <Text style={styles.viewAllText}>View all →</Text>
              </Pressable>
            )}
          </View>

          <View style={styles.activityCard}>
            {topFiveNotifs.length > 0 ? (
              topFiveNotifs.map((n) => (
                <NotificationCard
                  key={n.id}
                  notification={n}
                  onPress={() => handleNotifPress(n)}
                  compact
                  actions={notifActions.buildActionsFor(n)}
                  state={notifActions.deriveStateFor(n)}
                />
              ))
            ) : (
              <View style={styles.emptyState}>
                <MaterialIcons
                  name={
                    notifsLoading ? 'hourglass-empty' : 'notifications-none'
                  }
                  size={28}
                  color={fpColors.outline}
                />
                <Text style={styles.emptyText}>
                  {notifsLoading
                    ? 'Loading activity…'
                    : "You're all caught up. Activity from your circles, shares, and borrows will appear here."}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Phase D.2 — "Recent shares I sent" mini-section.
            Answers the user's "where do I see what I shared?" gap by
            surfacing the top 3 outbound active shares directly on the
            Dashboard. Same data the Share-tab "Shares I sent" section
            uses; this is a discovery shortcut. Hidden when empty so
            new users see no chrome. */}
        {myActiveShares.length > 0 && (
          <View style={styles.section} testID="dashboard-recent-shares-sent">
            <View style={styles.activityHeader}>
              <View style={styles.activityHeaderLeft}>
                <MaterialIcons
                  name="outbox"
                  size={18}
                  color={fpColors.primary}
                />
                {/* FE-205 — renamed from "Shares I sent" */}
                <Text style={styles.sectionTitle}>Lent Out</Text>
                {myActiveSharesCount > 0 && (
                  <View style={styles.unreadPill}>
                    <Text style={styles.unreadPillText}>
                      {myActiveSharesCount > 99
                        ? '99+'
                        : String(myActiveSharesCount)}
                    </Text>
                  </View>
                )}
              </View>
              <Pressable
                onPress={() => goTo('MyShares', { tab: 'sent' })}
                hitSlop={8}
                accessibilityRole="button"
                accessibilityLabel="View all sent shares"
              >
                <Text style={styles.viewAllText}>View all →</Text>
              </Pressable>
            </View>
            <View style={styles.activityCard}>
              {myActiveShares.slice(0, 3).map((s: SharedByMe) => (
                <ShareRow
                  key={s.share_id}
                  shareId={s.share_id}
                  itemName={s.item_name}
                  status={s.status}
                  sharingType={s.sharing_type}
                  createdAt={s.created_at}
                  expectedReturnDate={s.expected_return_date}
                  counterparty={s.shared_with}
                  direction="outbound"
                  onPress={() =>
                    // PR19 — Prefer ItemDetail when BE#179 exposed
                    // item_id on the share row (the common case
                    // today). Fall back to the MyShares page only
                    // for legacy rows where item_id is null (backing
                    // item deleted, etc.).
                    s.item_id != null
                      ? goTo('ItemDetail', { itemId: s.item_id })
                      : goTo('MyShares', { tab: 'sent' })
                  }
                />
              ))}
            </View>
          </View>
        )}
      </ScrollView>

      {/* FE-227 v2 — decline-with-notes modal for the Recent Activity
          feed's BORROW_REQUEST_RECEIVED cards. */}
      <DenyNotesSheet
        visible={notifActions.denySheet.visible}
        request={notifActions.denySheet.request}
        onClose={notifActions.denySheet.onClose}
      />
    </View>
  );
};

/* ————— styles ————— */

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: fpColors.background,
  },
  topBar: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
  },
  topBarInner: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
  },
  topBarRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: fpColors.primaryContainer,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onPrimaryContainer,
    letterSpacing: 0.5,
  },

  scroll: {
    padding: 16,
    paddingBottom: 32,
  },

  // FE-213 — Attention banner styles. Yellow (tertiaryContainer) by
  // default; flips to red (errorContainer) when there's an active
  // recall mixed in. Whole row is tappable → BorrowRequestsManager.
  attentionBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 14,
    backgroundColor: (fpColors as any).tertiaryContainer ?? '#FFE0B2',
    borderWidth: 1,
    borderColor: (fpColors as any).outlineVariant ?? '#E7E0EC',
    marginBottom: 16,
  },
  attentionBannerUrgent: {
    backgroundColor: (fpColors as any).errorContainer ?? '#FFDAD6',
  },
  attentionBannerIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: (fpColors as any).surface ?? '#FFFFFF',
  },
  attentionBannerIconUrgent: {
    backgroundColor: (fpColors as any).surface ?? '#FFFFFF',
  },
  attentionBannerTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  attentionBannerSubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
  },
  // FE #198 / Sub 3 — Overflow link when the viewer holds >3 items
  // with active recalls. Keeps the banner stack tight + readable.
  recallMoreLink: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    alignItems: 'center',
    marginTop: -4,
    marginBottom: 12,
  },
  recallMoreText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    color: fpColors.error,
    textDecorationLine: 'underline',
  },

  hero: {
    marginBottom: 20,
    gap: 14,
  },
  heroDesktop: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  heroText: {
    flex: 1,
  },
  h1: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 26,
    letterSpacing: -0.5,
    color: fpColors.onSurface,
    marginBottom: 4,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 20,
    color: fpColors.onSurfaceVariant,
  },
  heroCtaMobile: { width: '100%' },
  heroCtaDesktop: { minWidth: 220 },

  section: {
    marginBottom: 20,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onSurface,
  },
  sectionLink: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.primary,
  },

  // Sprint 8 / FE-129 — Recent activity (replaces Low Stock Alerts +
  // the fake last-5-items-added strip).
  activityHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  activityHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  unreadPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    backgroundColor: fpColors.primaryContainer,
  },
  unreadPillText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    color: fpColors.onPrimaryContainer,
    letterSpacing: 0.3,
  },
  viewAllText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.primary,
  },
  activityCard: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 14,
    overflow: 'hidden',
  },

  emptyState: {
    padding: 28,
    alignItems: 'center',
    gap: 10,
  },
  emptyText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
  },
});
